import { useEffect, type ComponentType } from 'react'

import { useAuth } from 'src/app'

import type { UseAuthParams } from 'src/app/hooks/useAuth/useAuth'
import type { User } from 'src/app/types'
import withCosmosStyles from 'src/config/cosmos/withCosmosStyles'
import api from 'src/config/request'
import { I18nProvider } from 'src/config/translation'
import { ThemeProvider } from 'src/lib/contexts'

import { useResetRequestConfig } from 'src/lib/hooks'

import { ErrorBoundary } from '../errorBoundary'

import { Loading } from '../loading'
import 'src/styles.css'

const withFederationConfig = <P extends JSX.IntrinsicAttributes>(
  WrappedComponent: ComponentType<P>,
  withAuth = false
): ((props: P) => JSX.Element) => {
  if (
    window?.__SENTRY_MICRO__?.instances &&
    !window?.__SENTRY_MICRO__?.instances?.['app-membership-community-management'] &&
    (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test')
  ) {
    import('src/config/sentry/sentry.config').then(({ default: initSentryClient }) => {
      initSentryClient()
    })
  }

  const ComponentWithExtraInfo = (props: P & UseAuthParams): JSX.Element => {
    const { user, isLoggedIn } = useAuth({ parentAppUser: props.parentAppUser || ({} as User) })
    const { isResetting } = useResetRequestConfig({ token: props.parentAppUser?.token ?? '' })

    useEffect(() => {
      if (!user?.token) return

      api.defaults.headers.common.Authorization = `Bearer ${user?.token}`
    }, [user?.token])

    if (withAuth && (!isLoggedIn || isResetting)) {
      return <Loading />
    }

    return (
      <ErrorBoundary>
        <I18nProvider>
          <ThemeProvider>
            <WrappedComponent {...props} />
          </ThemeProvider>
        </I18nProvider>
      </ErrorBoundary>
    )
  }

  return withCosmosStyles<P>(ComponentWithExtraInfo)
}

export default withFederationConfig
